// IndexedDB wrapper — three object stores for the v2 client.
//
// Decision (Session 19E):
//   - Three stores:
//       drafts   keyPath="id"          value=CourseDraft       — form output
//       pdfs     out-of-line key=sha256 value=Blob              — uploaded source bytes
//       results  keyPath="courseId"    value=IngestResultRecord — cached ingest output
//   - Out-of-line key for pdfs (sha256) so multiple drafts that share a PDF
//     can reference the same blob (de-duplication).
//   - Promise-wrapped, no third-party dep — IDB API surface we use is small.
//   - On open failure (private mode etc.), all calls reject; UI degrades to
//     "session-only" mode (drafts visible until reload). Better than crash.
//
// Schema versioning: bump DB_VERSION + handle migration in onupgradeneeded.

import type {
  CourseDraft,
  CourseProgress,
  IngestPackage,
  QualityAnnotation,
  SourceSlot,
} from '@examforge/shared/schemas';
import { emptyProgress, detectSlotFormat, migrateLegacyIngestPackage } from '@examforge/shared/schemas';
import { ERROR_EVENT_TYPES } from '@examforge/shared/api';
import { generateErrorId } from '../lib/error-id.js';
// 19BN Phase 0: putDraft stamps anonId on drafts that lack it (single
// chokepoint for anon→email migration attribution).
import { getOrCreateAnonId } from '../auth/anon-id.js';
import type { IngestResult } from '../api/ingest.js';
import { IngestResultSchema } from '../api/ingest.js';

const DB_NAME = 'examforge.v1';
// Session 19G (§5.1): bump to add the `progress` store.
// Session 19AS (G8): bump to 3 for `events` store — operator-facing event log
// (flag / rating / runtime-error / render-error / unhandled-rejection).
// Sprint 19BG+1 Phase 4: bump to 4 for `qualityAnnotations` store — human-scored
// rubric evaluations of AI-produced items (admin/eval annotation surface).
// Sprint 19BH Day 5 Step 5.1: bump to 5 for v3→v4 IngestPackage migration —
// rewrites every stored result through migrateLegacyIngestPackage so items
// gain caseIds/topicIds/conceptIds arrays, packages gain curriculumOutline
// (synthesized from cases if absent) and gapReport (default empty). Singular
// alias fields (caseId / conceptTag / topicId) are preserved so 19BH+1
// frontend code reads keep working until the 19BI clean-up.
const DB_VERSION = 6;

const STORE_DRAFTS = 'drafts';
const STORE_PDFS = 'pdfs';
const STORE_RESULTS = 'results';
const STORE_PROGRESS = 'progress';
const STORE_EVENTS = 'events';
const STORE_QUALITY_ANNOTATIONS = 'qualityAnnotations';
/** 19BN Phase 3: offline sync outbox. Pending sync operations queued while
 * offline; drained when network restores. */
const STORE_SYNC_QUEUE = 'syncQueue';

export interface IngestResultRecord {
  /** Matches CourseDraft.id — the draft that produced this result. */
  courseId: string;
  /** When the ingest completed. ISO datetime. */
  generatedAt: string;
  /** The full ingest result the worker returned. */
  result: IngestResult;
}

/**
 * 19AT · S2: typed error so callers (and the IdbHealthBanner) can distinguish
 * "no IDB at all" from a transient transaction failure.
 *
 * Causes seen in the wild:
 *   - Safari Private Mode (open works but quota=0)
 *   - Firefox in private mode (open returns null silently)
 *   - Disk full / quota exceeded
 *   - User disabled cookies + storage in browser settings
 *   - Embedded webview without storage permission
 *
 * Recovery posture: degrade to "session-only" — the in-memory state still
 * works, edits just don't persist across reload. UI surfaces a sticky toast.
 */
export class IdbUnavailableError extends Error {
  override readonly name = 'IdbUnavailableError';
  override readonly cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.cause = cause;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;
let dbAvailable: boolean | null = null;
let dbAvailabilityListeners: Array<(available: boolean) => void> = [];

/** Synchronous check after first openDB attempt. null = not yet probed. */
export function isIdbAvailable(): boolean | null {
  return dbAvailable;
}

/** Subscribe to availability changes (fires once after first probe). */
export function subscribeIdbAvailability(cb: (available: boolean) => void): () => void {
  dbAvailabilityListeners.push(cb);
  if (dbAvailable !== null) cb(dbAvailable);
  return () => {
    dbAvailabilityListeners = dbAvailabilityListeners.filter((c) => c !== cb);
  };
}

function setDbAvailability(value: boolean): void {
  if (dbAvailable === value) return;
  dbAvailable = value;
  for (const cb of dbAvailabilityListeners) {
    try { cb(value); } catch { /* don't let one bad listener kill the chain */ }
  }
}

/**
 * Sprint 19BH Day 5 Step 5.1 · v3→v4 IngestResultRecord migration.
 *
 * Called from onupgradeneeded when DB_VERSION jumps from <5 to 5. Iterates
 * every record in STORE_RESULTS with a cursor and rewrites in-place through
 * migrateLegacyIngestPackage (shared/migrate-legacy.ts), which forward-fills:
 *   - item-tag arrays (caseIds/topicIds/conceptIds) wrapping v3 singular fields
 *   - CurriculumOutline derived from cases (+ topicHierarchy) if absent
 *   - default empty GapReport if absent
 *   - schemaVersion bumped to '0.3.0' once the above rules fire
 *
 * Operates inside the versionchange transaction passed by the browser. Errors
 * on individual cursor reads are logged but do not abort the migration —
 * a single bad record shouldn't lock the user out of every other course. The
 * underlying migrateLegacyIngestPackage is idempotent and JSON-deep-clones
 * before mutation so partial failure cannot corrupt source-of-truth.
 *
 * Why a separate function (not inlined): keeps onupgradeneeded readable;
 * lets fake-indexeddb tests exercise the cursor walk end-to-end through the
 * normal openDB() path.
 */
function migrateResultsV3ToV4InTxn(txn: IDBTransaction): void {
  if (!txn.objectStoreNames.contains(STORE_RESULTS)) return;
  const store = txn.objectStore(STORE_RESULTS);
  const cursorReq = store.openCursor();
  cursorReq.onsuccess = () => {
    const cursor = cursorReq.result;
    if (!cursor) return;
    try {
      const record = cursor.value as IngestResultRecord | undefined;
      if (record && record.result && record.result.package) {
        const { migrated, applied } = migrateLegacyIngestPackage(record.result.package);
        if (applied.length > 0) {
          const updated: IngestResultRecord = {
            ...record,
            result: {
              ...record.result,
              package: migrated as IngestResultRecord['result']['package'],
            },
          };
          cursor.update(updated);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[migrateResultsV3ToV4InTxn] skipping record:', err);
    }
    cursor.continue();
  };
  cursorReq.onerror = () => {
    // eslint-disable-next-line no-console
    console.warn('[migrateResultsV3ToV4InTxn] cursor failed:', cursorReq.error);
  };
}

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      setDbAvailability(false);
      reject(new IdbUnavailableError('IndexedDB not available in this environment'));
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      setDbAvailability(false);
      reject(new IdbUnavailableError('indexedDB.open() threw', e));
      return;
    }
    req.onerror = () => {
      setDbAvailability(false);
      reject(new IdbUnavailableError(req.error?.message ?? 'open failed', req.error));
    };
    req.onblocked = () => {
      // Another tab holds an older version open — caller should reload.
      setDbAvailability(false);
      reject(new IdbUnavailableError('IndexedDB upgrade blocked by another tab — close other tabs and refresh'));
    };
    req.onsuccess = () => {
      setDbAvailability(true);
      resolve(req.result);
    };
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion;
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        db.createObjectStore(STORE_DRAFTS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_PDFS)) {
        db.createObjectStore(STORE_PDFS); // out-of-line key (sha256 supplied at put-time)
      }
      if (!db.objectStoreNames.contains(STORE_RESULTS)) {
        db.createObjectStore(STORE_RESULTS, { keyPath: 'courseId' });
      }
      if (!db.objectStoreNames.contains(STORE_PROGRESS)) {
        // Session 19G: per-course user progress (srs / wrongAnswers / quizHistory).
        db.createObjectStore(STORE_PROGRESS, { keyPath: 'courseId' });
      }
      if (!db.objectStoreNames.contains(STORE_EVENTS)) {
        // Session 19AS (G8): time-series event log. Auto-incrementing key (id),
        // indexed by occurredAt for chronological queries + by type for filter.
        const evStore = db.createObjectStore(STORE_EVENTS, { keyPath: 'id', autoIncrement: true });
        evStore.createIndex('occurredAt', 'occurredAt');
        evStore.createIndex('type', 'type');
        evStore.createIndex('courseId', 'courseId');
      }
      if (!db.objectStoreNames.contains(STORE_QUALITY_ANNOTATIONS)) {
        // Sprint 19BG+1 Phase 4: human-scored rubric evaluations.
        // keyPath='id' (uuid v4) so multi-reviewer / multi-timestamp annotations
        // for the same item coexist (no key collision).
        // - itemKey composite index: list all annotations for one item
        //   (across reviewers / over time).
        // - batch index: pull all annotations for a sample batch
        //   (Day 2 cs3052 PER-PAIR / Day 3 IB Tier 3 candidate batches).
        // - updatedAt index: server-side sync (push only since lastSync).
        const qaStore = db.createObjectStore(STORE_QUALITY_ANNOTATIONS, { keyPath: 'id' });
        qaStore.createIndex('itemKey', ['itemKind', 'itemId'], { unique: false });
        qaStore.createIndex('batch', 'source.batch');
        qaStore.createIndex('updatedAt', 'updatedAt');
      }
      // 19BN Phase 3: sync queue outbox.
      if (!db.objectStoreNames.contains(STORE_SYNC_QUEUE)) {
        const sqStore = db.createObjectStore(STORE_SYNC_QUEUE, { keyPath: 'id' });
        sqStore.createIndex('createdAt', 'createdAt');
        sqStore.createIndex('status', 'status');
      }
      // Sprint 19BH Day 5 Step 5.1: v3→v4 results migration. Runs once per
      // browser when DB jumps from <5 to 5. New installs (oldVersion === 0)
      // skip this — empty store has nothing to migrate. The cursor walk runs
      // inside the versionchange transaction the browser already started for
      // us; req.transaction is the right handle (NOT a new db.transaction()
      // call, which would deadlock against versionchange).
      if (oldVersion > 0 && oldVersion < 5) {
        const txn = req.transaction;
        if (txn) migrateResultsV3ToV4InTxn(txn);
      }
    };
  });
  // Reset promise on hard failure so a later call can retry.
  dbPromise.catch(() => {
    dbPromise = null;
  });
  return dbPromise;
}

function run<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(storeName, mode);
        const s = t.objectStore(storeName);
        const r = fn(s);
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error ?? new Error('IDB request failed'));
      }),
  );
}

// ─── Drafts ──────────────────────────────────────────────────────────────────

/**
 * Persist a CourseDraft. 19BN Phase 0: every draft now carries the user's
 * anonId so future anon→email migration can claim local drafts. The field
 * is filled here (single chokepoint) rather than at each of the 5 call sites
 * — getOrCreateAnonId is idempotent, so re-saves are no-ops on the field.
 *
 * B5.4 follow-up (DEBT-19BO+-DRAFT-MARKPENDING-WIREUP · 2026-07-03): also
 * flip the save-slice to 'pending' here. Every putDraft call site (create /
 * edit / import) is a cloud-syncable write, so the SyncStatusChip should
 * reflect it. Mirrors progress-sync-hook.ts:53's markPending pattern.
 *
 * Why dynamic import: store/index.ts transitively imports idb (via
 * regen-slice), so a static `import { useStore }` here would create a
 * storage↔store cycle. The dynamic import breaks the cycle at call time.
 * Harmless for anonymous users — the sync engine's debounce effect
 * (use-sync-engine.ts:128) re-checks isAuthed before enqueuing, and anon
 * users render no chip.
 */
export function putDraft(draft: CourseDraft): Promise<void> {
  const withAnon: CourseDraft = draft.anonId ? draft : { ...draft, anonId: getOrCreateAnonId() };
  // Fire-and-forget: markPending is a synchronous Zustand set; never block the
  // IDB write on it, and never let a store error break persistence. Gate on
  // authed user — anonymous writes are local-only, so flipping saveStatus to
  // 'pending' would leave it stuck (no drain happens for anon). Mirrors
  // progress-sync-hook.ts:42's userId gate.
  void import('../lib/store/index.js')
    .then(({ useStore }) => {
      const u = useStore.getState().currentUser;
      if (u && !u.isAnonymous) useStore.getState().markPending();
    })
    .catch(() => { /* store unavailable (SSR / test without provider) — non-critical */ });
  return run<IDBValidKey>(STORE_DRAFTS, 'readwrite', (s) => s.put(withAnon)).then(() => undefined);
}

export function getDraft(id: string): Promise<CourseDraft | undefined> {
  return run<CourseDraft | undefined>(STORE_DRAFTS, 'readonly', (s) =>
    s.get(id) as IDBRequest<CourseDraft | undefined>,
  ).then((d) => (d ? migrateDraft(d) : undefined));
}

export function listDrafts(): Promise<CourseDraft[]> {
  return run<CourseDraft[]>(STORE_DRAFTS, 'readonly', (s) =>
    s.getAll() as IDBRequest<CourseDraft[]>,
  ).then((all) => all.map(migrateDraft));
}

/**
 * Forward-fill required fields added in later schema versions onto drafts
 * persisted under earlier shapes. Session 19S §C-1 added `masteryLevel` +
 * `mockPapers` as REQUIRED — old drafts on disk lack them. Without this
 * shim, CourseSettingsView crashes on `draft.mockPapers.length` and the
 * page goes blank (Session 19Z 陛下: "点击课程设置后跳转一片空白").
 *
 * Strategy: shallow-merge defaults onto unknown-shape input, then trust the
 * caller. We don't safeParse here — that would reject old drafts entirely.
 *
 * Extension: when a future schema bump adds another required field, append
 * one line below. When a field is REMOVED, delete it from the draft here too.
 */
function migrateDraft(d: CourseDraft): CourseDraft {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = d as any;
  let next = d;
  if (raw.masteryLevel === undefined) {
    next = { ...next, masteryLevel: 'partial' };
  }
  if (!Array.isArray(raw.mockPapers)) {
    next = { ...next, mockPapers: [] };
  }
  // Session 19AD: convert legacy { source, pastPapers[] } → sources[].
  // Old MockPaperSpec.pastPaperRef ids (e.g. 'past_<sha8>') are preserved
  // as SourceSlot.id so back-references stay intact.
  if (!Array.isArray(raw.sources) || raw.sources.length === 0) {
    const sources: SourceSlot[] = [];
    if (raw.source && typeof raw.source.sha256 === 'string') {
      sources.push({
        id: `src_${raw.source.sha256.slice(0, 8)}`,
        kind: 'textbook',
        format: detectSlotFormat(raw.source.filename ?? ''),
        filename: raw.source.filename ?? 'source.pdf',
        sha256: raw.source.sha256,
        byteLength: raw.source.byteLength ?? 0,
      });
    }
    for (const p of (raw.pastPapers ?? []) as Array<{
      id?: string; filename?: string; sha256?: string; byteLength?: number;
      year?: number; label?: string;
    }>) {
      if (typeof p.sha256 !== 'string') continue;
      sources.push({
        id: p.id ?? `past_${p.sha256.slice(0, 8)}`,
        kind: 'past_paper',
        format: detectSlotFormat(p.filename ?? ''),
        filename: p.filename ?? 'past-paper.pdf',
        sha256: p.sha256,
        byteLength: p.byteLength ?? 0,
        ...(typeof p.year === 'number' && { year: p.year }),
        ...(typeof p.label === 'string' && p.label.length > 0 && { label: p.label }),
      });
    }
    if (sources.length > 0) next = { ...next, sources };
  }
  // Keep `pastPapers` array shape too — some legacy callers (course-settings
  // delete path) still iterate it. Mirror it from sources where missing.
  if (!Array.isArray(raw.pastPapers)) {
    next = { ...next, pastPapers: [] };
  }
  return next;
}

export function deleteDraft(id: string): Promise<void> {
  return run<undefined>(STORE_DRAFTS, 'readwrite', (s) =>
    s.delete(id) as IDBRequest<undefined>,
  ).then(() => undefined);
}

// ─── PDFs ────────────────────────────────────────────────────────────────────

export function putPdf(sha256: string, blob: Blob): Promise<void> {
  return run<IDBValidKey>(STORE_PDFS, 'readwrite', (s) => s.put(blob, sha256)).then(() => undefined);
}

export function getPdf(sha256: string): Promise<Blob | undefined> {
  return run<Blob | undefined>(STORE_PDFS, 'readonly', (s) =>
    s.get(sha256) as IDBRequest<Blob | undefined>,
  );
}

export function deletePdf(sha256: string): Promise<void> {
  return run<undefined>(STORE_PDFS, 'readwrite', (s) =>
    s.delete(sha256) as IDBRequest<undefined>,
  ).then(() => undefined);
}

// ─── Results ─────────────────────────────────────────────────────────────────

export function putResult(record: IngestResultRecord): Promise<void> {
  // DEBT-19BO+-PUTRESULT-SCHEMA-GATE (批次 C3 · 2026-06-29): safeParse before
  // write. On failure we console.error the zod issues but STILL WRITE —
  // refusing would discard user content (worse than a stale-shape record that
  // the UI degrades on). The gate moves the error scene from "UI crashes on
  // next read" to "console has a traceable zod issue at write time".
  // mergeStageRegenerationResult funnels through here too, so its output is
  // gated for free (single chokepoint).
  const parsed = IngestResultSchema.safeParse(record.result);
  if (!parsed.success) {
    // eslint-disable-next-line no-console
    console.error(
      `[idb] putResult schema gate FAILED for courseId=${record.courseId} — writing anyway ` +
      `(refusing would lose user content). zod issues:`,
      parsed.error.issues,
    );
  }
  return run<IDBValidKey>(STORE_RESULTS, 'readwrite', (s) => s.put(record)).then(() => undefined);
}

export function getResult(courseId: string): Promise<IngestResultRecord | undefined> {
  return run<IngestResultRecord | undefined>(STORE_RESULTS, 'readonly', (s) =>
    s.get(courseId) as IDBRequest<IngestResultRecord | undefined>,
  );
}

export function deleteResult(courseId: string): Promise<void> {
  return run<undefined>(STORE_RESULTS, 'readwrite', (s) =>
    s.delete(courseId) as IDBRequest<undefined>,
  ).then(() => undefined);
}

// ─── Partial Stage Regeneration Merge (Session 19BG Day 3-4c) ───────────────
//
// Helper used by the upcoming partial-regen UI (DEBT-19BG-PARTIAL-REGEN-UI ·
// 19BG+1) to merge regenerated stage items into the existing IngestResultRecord
// without disturbing other stages or rewriting the entire package. Backend
// (POST /ingest/regenerate-stage → RegenerateStageWorkflow → /ingest/status)
// returns:
//   { partialRegen: true, stage, chapterId?, mode, items: [...] }
// Frontend reads that and calls this utility to persist into IDB.
//
// 不在范围（立账 19BG+1 与 UI 一起做）：history snapshot · undo · EditableItem wrapper.

/** Stages supported by partial regen merge. Subset of PHASE3_STAGES; mock_papers
 *  excluded — mockExamItems is a single object not an array, has different
 *  merge semantics (立账 19BG+1 if needed). */
export type PartialRegenStage =
  | 'mcqs' | 'true_false' | 'flashcards' | 'essays'
  | 'glossary' | 'mindmaps' | 'lecture_notes'
  | 'proof_drills' | 'free_responses' | 'reading_passages';

/** Map stage id → IngestPackage field name. Stage ids are snake_case (worker
 *  side convention); package fields are camelCase (frontend convention). */
const STAGE_TO_PACKAGE_FIELD: Record<PartialRegenStage, string> = {
  mcqs:              'mcqs',
  true_false:        'trueFalse',
  flashcards:        'flashcards',
  essays:            'essays',
  glossary:          'glossary',
  mindmaps:          'mindmaps',
  lecture_notes:     'lectureNotes',
  proof_drills:      'proofDrills',
  free_responses:    'freeResponses',
  reading_passages:  'readingPassages',
};

/**
 * Merge partial-regen result into the existing IngestResultRecord.
 *
 *   mode='replace' + chapterId  → drop items where caseId === chapterId, concat new
 *   mode='replace' no chapterId → overwrite the entire stage array with new items
 *   mode='add'                  → concat + dedupe by id (existing items kept on
 *                                  collision so user edits aren't overwritten)
 *
 * Atomic via single putResult call. Throws if the courseId has no existing
 * record (caller must full-ingest first; partial regen is incremental, not
 * standalone). Does NOT touch other stages, schemaVersion, cases, or any
 * non-stage IngestPackage field.
 */
export async function mergeStageRegenerationResult(
  courseId: string,
  stage: PartialRegenStage,
  items: readonly unknown[],
  mode: 'replace' | 'add',
  chapterId?: string,
): Promise<void> {
  const existing = await getResult(courseId);
  if (!existing) {
    throw new Error(
      `mergeStageRegenerationResult: no IngestResultRecord for courseId=${courseId}. ` +
      `Partial regen requires a prior full ingest result.`,
    );
  }

  const fieldName = STAGE_TO_PACKAGE_FIELD[stage];
  // IngestPackage is z-typed but we treat fields as loose Record here — partial
  // regen doesn't need to validate every nested field; backend already did.
  const pkg = existing.result.package as unknown as Record<string, unknown>;
  const currentItems =
    (pkg[fieldName] as Array<{ id: string; caseId?: string }> | undefined) ?? [];
  const newItems = items as Array<{ id: string; caseId?: string }>;

  let nextItems: Array<{ id: string; caseId?: string }>;
  if (mode === 'replace') {
    if (chapterId) {
      // Drop items belonging to this chapter only — keep other chapters' items intact.
      // Use caseIds (plural array) for matching; fall back to singular caseId for
      // pre-coerce data. Items with no chapter association are kept (conservative).
      const otherChapters = currentItems.filter((it) => {
        const rec = it as Record<string, unknown>;
        const ids = (rec['caseIds'] as string[] | undefined)
          ?? (rec['caseId'] ? [rec['caseId'] as string] : []);
        return !ids.includes(chapterId);
      });
      nextItems = [...otherChapters, ...newItems];
    } else {
      // Course-level replace: overwrite the entire field.
      nextItems = [...newItems];
    }
  } else {
    // mode === 'add' — concat + dedupe by id. Existing items are kept on
    // collision so user edits via UI (post-ingest field tweaks) survive.
    const existingIds = new Set(currentItems.map((it) => it.id));
    const additions = newItems.filter((it) => !existingIds.has(it.id));
    nextItems = [...currentItems, ...additions];
  }

  const updatedPkg = { ...pkg, [fieldName]: nextItems };
  const updatedRecord: IngestResultRecord = {
    ...existing,
    generatedAt: new Date().toISOString(),
    result: {
      ...existing.result,
      // Type assertion (not `as any`): pkg entered as Record<string,unknown>
      // and we only swapped one stage array. Runtime validity is gated by
      // putResult's IngestResultSchema.safeParse below (DEBT-19BO+-PUTRESULT-
      // SCHEMA-GATE) — this assertion is purely to satisfy TS structurally.
      package: updatedPkg as IngestPackage,
    },
  };
  await putResult(updatedRecord);
}

// ─── Progress (Session 19G) ─────────────────────────────────────────────────

/** Read progress; returns an empty-progress object if the course has none yet.
 *
 *  Session 19H: also forward-fills any new fields (bookmarks, activeQuizSession)
 *  on records persisted before those fields existed, so callers can rely on
 *  `progress.bookmarks` / `progress.activeQuizSession` being defined. */
export async function getProgress(courseId: string): Promise<CourseProgress> {
  const existing = await run<CourseProgress | undefined>(
    STORE_PROGRESS,
    'readonly',
    (s) => s.get(courseId) as IDBRequest<CourseProgress | undefined>,
  );
  if (!existing) return emptyProgress(courseId);
  // Forward-fill missing fields (no DB version bump needed — additive only).
  const defaults = emptyProgress(courseId);
  return { ...defaults, ...existing };
}

export function putProgress(progress: CourseProgress): Promise<void> {
  return run<IDBValidKey>(STORE_PROGRESS, 'readwrite', (s) => s.put(progress)).then(() => undefined);
}

/**
 * Read-modify-write helper. Used by SRS / wrong-answer / quiz-session writers.
 * The `mutate` callback may return a new object; if it returns void, mutations
 * to the passed-in object are kept. Always stamps `updatedAt` before writing.
 *
 * 19BO+ B4.4: after writing, fires `progressSyncHook` (if configured) with the
 * before/after snapshots. The hook diffs them into ProgressEvents and enqueues
 * to the sync outbox — but ONLY when the user has opted into progress cloud
 * sync (step 4 wires the opt-in gate). Default hook is a no-op so this is
 * inert until step 4 activates it.
 */
export async function updateProgress(
  courseId: string,
  mutate: (p: CourseProgress) => CourseProgress | void,
): Promise<CourseProgress> {
  const before = await getProgress(courseId);
  // Snapshot before the mutate runs — callers commonly mutate the passed-in
  // object in place (p.wrongAnswers[id] = ...), so `before` and the result
  // would alias the same reference and the diff would see no change. The
  // snapshot is only consumed by progressSyncHook; if no hook is set this is
  // a cheap structuredClone on a typically-small object.
  const beforeSnapshot = progressSyncHook ? structuredClone(before) : before;
  const result = mutate(before);
  const after = result ?? before;
  after.updatedAt = new Date().toISOString();
  await putProgress(after);
  // Fire-and-forget: sync enqueue must never block or break a learning write.
  try {
    progressSyncHook?.(courseId, beforeSnapshot, after);
  } catch {
    // non-critical — a hook error must not surface to the learner
  }
  return after;
}

/**
 * Hook fired after every updateProgress write. Step 4 sets this to a function
 * that (a) checks the opt-in toggle, (b) diffs before→after, (c) enqueues
 * ProgressEvents to STORE_SYNC_QUEUE. Until then it is null (sync inert).
 *
 * Module-level (not per-call) so there is a single configuration point —
 * matches the bindEmailEnv pattern in notifications.ts. Tests reset via
 * _setProgressSyncHookForTest(null).
 */
type ProgressSyncHook = (courseId: string, before: CourseProgress, after: CourseProgress) => void;
let progressSyncHook: ProgressSyncHook | null = null;

/** Configure the post-write progress-sync hook (step 4 activation point). */
export function setProgressSyncHook(hook: ProgressSyncHook | null): void {
  progressSyncHook = hook;
}

/** Test-only alias to reset the hook (semantic clarity in test setup). */
export const _setProgressSyncHookForTest = setProgressSyncHook;

export function deleteProgress(courseId: string): Promise<void> {
  return run<undefined>(STORE_PROGRESS, 'readwrite', (s) =>
    s.delete(courseId) as IDBRequest<undefined>,
  ).then(() => undefined);
}

/** Session 19AR · G5: cross-course progress dump for the operator
 *  hallucination dashboard. DEV-only consumer. */
export function listAllProgress(): Promise<CourseProgress[]> {
  return run<CourseProgress[]>(STORE_PROGRESS, 'readonly', (s) =>
    s.getAll() as IDBRequest<CourseProgress[]>,
  );
}

/** Session 19AR · G5: cross-course IngestPackage dump (used to look up the
 *  flagged item's content for display in the dashboard). DEV-only consumer. */
export function listAllResults(): Promise<IngestResultRecord[]> {
  return run<IngestResultRecord[]>(STORE_RESULTS, 'readonly', (s) =>
    s.getAll() as IDBRequest<IngestResultRecord[]>,
  );
}

// ─── Quality Annotations (Sprint 19BG+1 Phase 4) ─────────────────────────────
//
// Human-scored rubric evaluations for the admin/eval annotation surface. See
// `packages/shared/src/schemas/quality-annotation.ts` for the schema and
// `rubric-v1.ts` for the dimension definitions.
//
// Storage stance:
//   - Append-only-ish — `putAnnotation` upserts by id; deletion is intentional
//     and rare (rubric data is research evidence, not transient state).
//   - Multi-reviewer ready — keyPath='id' allows N annotations per (itemKind,
//     itemId) tuple. v1 only writes `reviewerId='local'`; server-side mode
//     fills real ids without schema change.
//   - Indexed access — list-for-item (cross-reviewer comparison) and
//     list-by-batch (whole-batch export to CSV) are common query paths;
//     updatedAt index supports incremental sync.
//
// What we DO NOT do here:
//   - No schema parsing on read — IDB is trusted; server-side import does the
//     parse (see admin/eval-export.ts).
//   - No deletion of stale rubric versions — old vintage annotations stay,
//     UI badge-marks them.

export function putAnnotation(annotation: QualityAnnotation): Promise<void> {
  return run<IDBValidKey>(STORE_QUALITY_ANNOTATIONS, 'readwrite', (s) =>
    s.put(annotation),
  ).then(() => undefined);
}

export function getAnnotation(id: string): Promise<QualityAnnotation | undefined> {
  return run<QualityAnnotation | undefined>(STORE_QUALITY_ANNOTATIONS, 'readonly', (s) =>
    s.get(id) as IDBRequest<QualityAnnotation | undefined>,
  );
}

/** All annotations for a sample batch (e.g. 'cs3052-day2-cdf563d1'). */
export function listAnnotationsByBatch(batch: string): Promise<QualityAnnotation[]> {
  return run<QualityAnnotation[]>(STORE_QUALITY_ANNOTATIONS, 'readonly', (s) =>
    s.index('batch').getAll(batch) as IDBRequest<QualityAnnotation[]>,
  );
}

/** All annotations for a single item across reviewers + time. Used for
 *  inter-rater agreement display (currently stub). */
export function listAnnotationsForItem(
  itemKind: QualityAnnotation['itemKind'],
  itemId: string,
): Promise<QualityAnnotation[]> {
  return run<QualityAnnotation[]>(STORE_QUALITY_ANNOTATIONS, 'readonly', (s) =>
    s.index('itemKey').getAll(IDBKeyRange.only([itemKind, itemId])) as
      IDBRequest<QualityAnnotation[]>,
  );
}

/** All annotations in the store. DEV / export only — prefer batch-scoped
 *  queries for normal use. */
export function listAllAnnotations(): Promise<QualityAnnotation[]> {
  return run<QualityAnnotation[]>(STORE_QUALITY_ANNOTATIONS, 'readonly', (s) =>
    s.getAll() as IDBRequest<QualityAnnotation[]>,
  );
}

export function deleteAnnotation(id: string): Promise<void> {
  return run<undefined>(STORE_QUALITY_ANNOTATIONS, 'readwrite', (s) =>
    s.delete(id) as IDBRequest<undefined>,
  ).then(() => undefined);
}

// ─── Events (Session 19AS · G8) ──────────────────────────────────────────────

/**
 * EventRecord — append-only telemetry for the operator dashboard.
 *
 * Categories captured:
 *   - 'flag' / 'rating' / 'note' — explicit user feedback (companion to
 *     itemFeedback; events provide chronology + context, itemFeedback the
 *     latest state).
 *   - 'runtime-error' — window.onerror caught (top-level synchronous throw).
 *   - 'unhandled-rejection' — window.onunhandledrejection (async).
 *   - 'render-error' — React ErrorBoundary's componentDidCatch.
 *   - 'autosave' — when EditableText debounce-commits (lets the operator see
 *     edit pace + which items got reworked most).
 *   - 'navigation' — route change (helps reconstruct what user was doing
 *     before an error). Optional / opt-in.
 *
 * Append-only: never edited or deleted automatically. Operator can manually
 * clear via the dashboard.
 */
export type EventType =
  | 'flag' | 'rating' | 'note'
  | 'runtime-error' | 'unhandled-rejection' | 'render-error'
  | 'autosave' | 'navigation' | 'autosave-error'
  | 'api-error' // 19BL: HTTP failures caught by the unified api-client (postV4/getV4)
  | 'behavior-error' // 19BM: non-fatal component behavior failures previously swallowed by try/catch
  | 'subjective-feedback'; // 19BM-F: user-initiated "this feels off" feedback via Cmd+/ hotkey. NOT an error — deliberately excluded from ERROR_EVENT_TYPES so it doesn't get an errorId stamp or pollute the error-centric feed.

export interface EventRecord {
  /** Auto-incrementing IDB primary key. */
  id?: number;
  type: EventType;
  /** ISO datetime when event occurred. */
  occurredAt: string;
  /** Human-readable summary (1 line). */
  summary: string;
  /** Free-form payload for type-specific detail. */
  payload?: Record<string, unknown>;
  /** When attached to a course / item. */
  courseId?: string;
  itemId?: string;
  itemField?: string;
  /** URL pathname when the event fired (helps reconstruct context). */
  viewPath?: string;
  /** Browser user-agent — populated automatically. */
  userAgent?: string;
}

// 19BL Session 2: error-typed events are the subset that gets a stamped
// errorId (see recordEvent). ERROR_EVENT_TYPES is the single source of truth
// shared with the backend's admin Errors view (Session 5) — keeping the
// filter in sync across IDB / D1 / admin is what makes the error feed clean.
const ERROR_TYPE_SET = new Set<string>(ERROR_EVENT_TYPES);
function isErrorEventType(type: string): boolean {
  return ERROR_TYPE_SET.has(type);
}

export function recordEvent(input: Omit<EventRecord, 'id' | 'occurredAt' | 'userAgent'> & { occurredAt?: string; userAgent?: string }): Promise<void> {
  const rec: EventRecord = {
    ...input,
    occurredAt: input.occurredAt ?? new Date().toISOString(),
    userAgent: input.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'),
  };
  // 19BL Session 2: error-typed events get a client-generated errorId stamped
  // into payload, and that id becomes the D1 row's primary key (so a flaky-
  // network retry is idempotent AND the user/operator can reference the error
  // by a stable handle). Stamped here — the single chokepoint all three error
  // entry points (ErrorBoundary / window.onerror / unhandledrejection) funnel
  // through — so every error path is covered by one change. Benign types
  // (flag/rating/note/navigation/autosave) are NOT stamped: they must not
  // pollute the error-centric feed, and the server mints a PK for them.
  const isError = isErrorEventType(rec.type);
  if (isError) {
    const errorId = generateErrorId();
    rec.payload = { ...(rec.payload ?? {}), errorId };
  }
  // 19BB-PROD: also push to D1 for cross-user admin visibility. Fire-and-forget.
  // Dynamic import keeps this hook out of the IDB module's initial bundle and
  // avoids cycles. Promise rejections are swallowed inside pushTelemetryEvent —
  // we still defensively `void`-discard here to satisfy lint.
  void import('../api/telemetry.js')
    .then((m) => m.pushTelemetryEvent({
      type: rec.type,
      summary: rec.summary,
      // 19BL: error events carry their id as the D1 PK (INSERT OR IGNORE on
      // the server makes retries idempotent). Benign events omit it so the
      // server mints one (backwards-compatible with pre-19BL callers).
      ...(isError && rec.payload?.['errorId']
        ? { id: rec.payload['errorId'] as string }
        : {}),
      ...(rec.courseId ? { courseId: rec.courseId } : {}),
      ...(rec.itemId ? { itemId: rec.itemId } : {}),
      ...(rec.itemField ? { itemField: rec.itemField } : {}),
      ...(rec.viewPath ? { viewPath: rec.viewPath } : {}),
      ...(rec.payload ? { payload: rec.payload } : {}),
      ...(typeof rec.payload?.['flagged'] === 'boolean' ? { flagged: rec.payload['flagged'] } : {}),
      ...(typeof rec.payload?.['rating'] === 'number' ? { rating: rec.payload['rating'] } : {}),
      ...(typeof rec.payload?.['note'] === 'string' ? { note: rec.payload['note'] } : {}),
    }))
    .catch(() => { /* never block IDB write on telemetry failure */ });
  return run<IDBValidKey>(STORE_EVENTS, 'readwrite', (s) => s.add(rec) as IDBRequest<IDBValidKey>)
    .then(() => undefined)
    // Don't let event-recording itself throw — telemetry must never break the app.
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[recordEvent] failed:', err);
    });
}

export function listEvents(opts: { limit?: number; type?: EventType; courseId?: string } = {}): Promise<EventRecord[]> {
  const { limit = 500, type, courseId } = opts;
  return run<EventRecord[]>(STORE_EVENTS, 'readonly', (s) => {
    // Use the occurredAt index to traverse most-recent first.
    return s.index('occurredAt').getAll() as IDBRequest<EventRecord[]>;
  }).then((all) => {
    const filtered = all
      .filter((e) => (type ? e.type === type : true))
      .filter((e) => (courseId ? e.courseId === courseId : true))
      .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
      .slice(0, limit);
    return filtered;
  });
}

export function clearEvents(): Promise<void> {
  return run<undefined>(STORE_EVENTS, 'readwrite', (s) => s.clear() as IDBRequest<undefined>).then(() => undefined);
}

// ─── Sync Queue (19BN Phase 3) ───────────────────────────────────────────────
// Offline sync outbox. Each entry is a pending cloud-sync operation.

export interface SyncQueueEntry {
  id: string;              // crypto.randomUUID()
  /** 'course' = draft+result opaque blob (19BN Phase 3).
   *  'progress' = learning-progress field-level events (19BO+ B4.4). */
  type: 'course' | 'progress';
  courseId: string;
  payload: unknown;        // serialized course data to PUT (course) or ProgressEvent[] (progress)
  status: 'pending' | 'synced' | 'failed';
  attempts: number;
  createdAt: number;
  lastAttemptAt?: number;
}

export function enqueueSync(entry: Omit<SyncQueueEntry, 'id' | 'status' | 'attempts' | 'createdAt'>): Promise<string> {
  const id = crypto.randomUUID();
  const full: SyncQueueEntry = { ...entry, id, status: 'pending', attempts: 0, createdAt: Date.now() };
  return run<IDBValidKey>(STORE_SYNC_QUEUE, 'readwrite', (s) => s.put(full))
    .then(() => id);
}

export function listPendingSync(): Promise<SyncQueueEntry[]> {
  return run<SyncQueueEntry[]>(STORE_SYNC_QUEUE, 'readonly', (s) =>
    s.index('status').getAll('pending') as IDBRequest<SyncQueueEntry[]>,
  );
}

export function updateSyncEntry(id: string, patch: Partial<SyncQueueEntry>): Promise<void> {
  return run<SyncQueueEntry | undefined>(STORE_SYNC_QUEUE, 'readonly', (s) =>
    s.get(id) as IDBRequest<SyncQueueEntry | undefined>,
  ).then(async (entry) => {
    if (!entry) return;
    const updated = { ...entry, ...patch };
    await run<IDBValidKey>(STORE_SYNC_QUEUE, 'readwrite', (s) => s.put(updated));
  });
}

export function clearSyncedEntries(): Promise<void> {
  // Delete all entries with status='synced'. Simplest approach: get all, filter, delete.
  return run<SyncQueueEntry[]>(STORE_SYNC_QUEUE, 'readonly', (s) =>
    s.index('status').getAll('synced') as IDBRequest<SyncQueueEntry[]>,
  ).then(async (synced) => {
    for (const entry of synced) {
      await run<undefined>(STORE_SYNC_QUEUE, 'readwrite', (s) => s.delete(entry.id) as IDBRequest<undefined>);
    }
  });
}
